"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _customHooks = require("../custom-hooks");
var _FirstTierBoundary = _interopRequireDefault(require("./FirstTierBoundary"));
var _SecondTierBoundary = _interopRequireDefault(require("./SecondTierBoundary"));
var _ThirdTierBoundary = _interopRequireDefault(require("./ThirdTierBoundary"));
var _FirstTier = _interopRequireDefault(require("./FirstTier"));
var _SecondTier = _interopRequireDefault(require("./SecondTier"));
var _ThirdTier = _interopRequireDefault(require("./ThirdTier"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
/**
 * A Navbar, using consumer theme, that supports data from a tree or Sitecore API
 *
 * You can find more info including swagger docs for each env here:
 *
 * https://massmutual.atlassian.net/wiki/spaces/DXCP/pages/4462871547/Sitecore+Header+API
 */
const NavbarConsumer = props => {
  const {
    collapseId,
    tree,
    href,
    searchNavId
  } = props;
  const searchToggleRef = (0, _react.useRef)(null);
  const contentRef = (0, _react.useRef)(null);
  const [navExpanded, setNavExpanded] = (0, _react.useState)(false);
  const [searchExpanded, setSearchExpanded] = (0, _react.useState)(false);
  const [scrollCursor, setScrollCursor] = (0, _react.useState)(0);
  const {
    data,
    status
  } = (0, _customHooks.useFetch)(href);
  const onNavToggleClick = () => {
    if (navExpanded) {
      setScrollCursor(0);
      setTimeout(() => {
        // Ignore the timeout; synchronous tests don't wait for callbacks.
        /* istanbul ignore next */
        window.scrollTo(0, scrollCursor);
      }, 100);
    } else {
      setScrollCursor(window.scrollY);
      window.scrollTo(0, 0);
    }
    setNavExpanded(!navExpanded);
    setSearchExpanded(false);
  };
  const onSearchToggleClick = () => {
    /* istanbul ignore next */
    if (searchExpanded) {
      if (searchToggleRef.current) {
        searchToggleRef.current.focus();
      }
    }
    setSearchExpanded(!searchExpanded);
    setNavExpanded(false);
  };

  // Collapse the content nav when a user scrolls the component out of the
  // viewport, especially useful on mobile.
  (0, _react.useEffect)(() => {
    // Scroll events are extremely difficult to test in the jsdom
    // and testing environments; these kinds of tests should be covered
    // by End to End (E2E) testing tools, like Cypress.
    /* istanbul ignore next */
    const scrollListener = () => {
      if (contentRef.current === null || !navExpanded) return;
      if (window.scrollY > contentRef.current.offsetTop + contentRef.current.clientHeight) {
        setNavExpanded(false);
      }
    };
    window.addEventListener('scroll', scrollListener);
    return () => {
      window.removeEventListener('scroll', scrollListener);
    };
  }, [contentRef, navExpanded]);
  const trunk = status === 'fetched' ? data : tree;

  // if no trunk or href, return just first tier
  // if either tree or href, go on to regular code

  if (trunk === undefined || Object.keys(trunk).length === 0) {
    return /*#__PURE__*/_react.default.createElement(_FirstTierBoundary.default, null, /*#__PURE__*/_react.default.createElement(_FirstTier.default, {
      expanded: navExpanded,
      handleClick: onNavToggleClick
    }));
  }

  // if the data shows that there are multiple columns in the mega menu, add the top level class
  const megaMenuCheck = trunk?.navheader?.megaNav?.find(dropdownData => dropdownData.navColumns !== undefined && dropdownData.navColumns.length >= 2);
  const navClass = megaMenuCheck !== undefined ? 'mm-navigation--mega' : 'mm-navigation';

  // Navbar is 'simple' when:
  // - navSettings.brandDisplayText is absent or empty ('')
  // - megaNav is absent or an empty list ([])
  // - utilityNav is absent or an empty list ([])
  const simple = !trunk?.navheader?.navSettings?.brandDisplayText && (!trunk?.navheader?.megaNav || trunk.navheader.megaNav.length === 0) && (!trunk?.navheader?.utilityNav || trunk.navheader.utilityNav.length === 0);
  const useSearch = trunk?.navheader?.navSettings?.showSearch;
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement("nav", {
    "aria-label": "banner navigation",
    className: navClass,
    title: "banner navigation"
  }, /*#__PURE__*/_react.default.createElement(_FirstTierBoundary.default, null, /*#__PURE__*/_react.default.createElement(_FirstTier.default, {
    collapseId: collapseId,
    navExpanded: navExpanded,
    onNavToggleClick: onNavToggleClick,
    onSearchToggleClick: onSearchToggleClick,
    searchExpanded: searchExpanded,
    searchNavId: searchNavId,
    ref: searchToggleRef,
    trunk: trunk
  }))), /*#__PURE__*/_react.default.createElement("nav", {
    "aria-label": "content-navigation",
    className: navClass,
    title: "content navigation",
    ref: contentRef
  }, /*#__PURE__*/_react.default.createElement(_SecondTierBoundary.default, {
    simple: simple
  }, /*#__PURE__*/_react.default.createElement(_SecondTier.default, {
    collapseId: collapseId,
    expanded: navExpanded,
    trunk: trunk
  }))), /*#__PURE__*/_react.default.createElement("nav", {
    "aria-label": "search navigation",
    className: navClass,
    title: "search navigation"
  }, useSearch && /*#__PURE__*/_react.default.createElement(_ThirdTierBoundary.default, null, /*#__PURE__*/_react.default.createElement(_ThirdTier.default, {
    expanded: searchExpanded,
    onSearchCancel: onSearchToggleClick,
    searchNavId: searchNavId,
    navSettings: trunk.navheader.navSettings
  }))));
};
var _default = exports.default = NavbarConsumer; // NavbarConsumer Props
NavbarConsumer.propTypes = {
  /** The ID of the Navbar Consumer component */
  collapseId: _propTypes.default.string,
  /** The API URL for the navbar data */
  href: _propTypes.default.string,
  /** The ID of the Search Nav area */
  searchNavId: _propTypes.default.string,
  /** The data object that is created from api response data */
  tree: _propTypes.default.object.isRequired
};
NavbarConsumer.defaultProps = {
  collapseId: 'main-nav--dropdowns',
  searchNavId: 'main-nav--search'
};